/**
 * Canonical puzzle timezone (Europe/Budapest). All date boundaries (streak,
 * daily archetype, stats submission) anchor to this TZ for v1.0/v1.1.
 * See spec §2.7.
 */
export const BUDAPEST_TZ = 'Europe/Budapest';

// Module-scope cache — Intl.DateTimeFormat construction is V8-cold ~1-3ms;
// caching matters under the Worker 10ms CPU/req limit.
const BUDAPEST_DATE_FMT = new Intl.DateTimeFormat('sv-SE', {
  timeZone: BUDAPEST_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Returns the YYYY-MM-DD calendar date in Europe/Budapest at the given moment.
 * Uses Intl.DateTimeFormat with the IANA TZ — handles CET/CEST DST automatically.
 */
export function puzzleDateBudapest(now: Date = new Date()): string {
  return BUDAPEST_DATE_FMT.format(now);
}

/**
 * Milliseconds until the next puzzle (next Budapest local midnight).
 * Drift-immune: callers should pass `new Date()` per tick rather than
 * decrement a stored interval.
 *
 * Implementation: get current Budapest YYYY-MM-DD, then binary-search to find
 * the UTC instant whose Budapest formatted date differs from today's. This
 * handles DST transitions automatically because Intl.DateTimeFormat with
 * timeZone: BUDAPEST_TZ reflects the live UTC offset. Both DST spring-forward
 * (23-hour day, CET→CEST) and fall-back (25-hour day, CEST→CET) converge
 * correctly because the search range is now .. now + 26 hours.
 */
export function msUntilNextPuzzleBudapest(now: Date = new Date()): number {
  const today = puzzleDateBudapest(now);
  // Binary-search the UTC ms boundary where the Budapest-local date flips off `today`.
  // Search range: now .. now + 26 hours (covers DST fall-back's 25-hour day with margin).
  let lo = now.getTime();
  let hi = lo + 26 * 3_600_000;
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    const d = puzzleDateBudapest(new Date(mid));
    if (d === today) lo = mid;
    else hi = mid;
  }
  return hi - now.getTime();
}
